import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, ChevronDown, ChevronRight, Check, Bug, Lightbulb, HelpCircle, MessageSquare, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { FeedbackSubmissionService } from '../../services/feedbackSubmissionService';
import { useScreenshotCapture } from '../../hooks/useScreenshotCapture';
import { useMetadata } from '../../hooks/useMetadata';
import { useKeyboardHandlers, useFocusAndGA, useDropdownClickOutside } from '../../hooks/useFeedbackModalHandlers';
import { FeedbackModalProps, FeedbackType, Toast } from '../../types/feedback';
import { FeedbackFormSection } from './components/FeedbackFormSection';
import { MetadataSection } from './components/MetadataSection';
import { MetadataContent } from './components/MetadataContent';
import ToastComponent from '../toast/Toast';
import { useFocusTrap } from '../../hooks/use-focus-trap';
import './FeedbackModal.scss';

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  rolePlayData,
  rolePlaySettings
}) => {
  const { t } = useTranslation();
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('suggestion');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showMetadata, setShowMetadata] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [gaSessionId, setGaSessionId] = useState<string | null>(null);
  const [gaClientId, setGaClientId] = useState<string | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const successHeadingRef = useRef<HTMLHeadingElement>(null);

  const { currentUser } = useAuth() as { currentUser: any };

  // Use extracted hook for screenshot functionality
  const {
    screenshot,
    screenshotError,
    isCapturingScreenshot,
    handleFileUpload,
    resetScreenshot
  } = useScreenshotCapture();

  // Use service for feedback submission
  const submissionService = new FeedbackSubmissionService();

  // Use metadata hook for cleaner metadata management
  const {
    generateMetadata,
    getMetadataItemCount: metadataItemCount,
    getCurrentPageName,
    getRolePlaySettingsMetadata
  } = useMetadata({
    currentUser,
    sessionId,
    rolePlayData,
    rolePlaySettings,
    screenshot
  });

  // Toast notification functions
  const addToast = (message: string, type: 'success' | 'error' | 'info') => {
    const id = Date.now().toString();
    setToasts(prevToasts => [...prevToasts, { id, message, type }]);
    return id;
  };

  const removeToast = (id: string) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  };

  const feedbackTypes = [
    {
      id: 'bug',
      label: t('appFeedback.typeBug'),
      description: t('appFeedback.typeBugDescription'),
      icon: Bug,
      helperText: t('appFeedback.typeBugHelper')
    },
    {
      id: 'suggestion',
      label: t('appFeedback.typeSuggestion'),
      description: t('appFeedback.typeSuggestionDescription'),
      icon: Lightbulb,
      helperText: t('appFeedback.typeSuggestionHelper')
    },
    {
      id: 'question',
      label: t('appFeedback.typeQuestion'),
      description: t('appFeedback.typeQuestionDescription'),
      icon: HelpCircle,
      helperText: t('appFeedback.typeQuestionHelper')
    },
    {
      id: 'other',
      label: t('appFeedback.typeOther'),
      description: t('appFeedback.typeOtherDescription'),
      icon: MessageSquare,
      helperText: t('appFeedback.typeOtherHelper')
    }
  ];



  // Handle form submission using extracted service
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedbackText.trim()) return;

    setIsSubmitting(true);

    try {
      const metadata = await generateMetadata();
      
      const feedbackData = {
        type: feedbackType,
        message: feedbackText.trim(),
        feedback_metadata: metadata
      };

      await submissionService.handleSubmissionWithNotification(
        feedbackData,
        () => addToast(t('appFeedback.uploadingScreenshot'), 'info'),
        () => {
          setIsSubmitted(true);
          window.dispatchEvent(new CustomEvent('feedback-submitted'));
          setTimeout(() => handleClose(), 2000);
        },
        (message) => addToast(message, 'error')
      );
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      addToast(t('appFeedback.submitFailed'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    setFeedbackText('');
    setFeedbackType('suggestion');
    setIsSubmitted(false);
    setIsSubmitting(false);
    setShowMetadata(false);
    setIsDropdownOpen(false);
    setToasts([]);
    setGaSessionId(null);
    setGaClientId(null);
    resetScreenshot();
    onClose();
  };

  // Use extracted hooks for event handlers
  useKeyboardHandlers({
    isOpen,
    feedbackText,
    isSubmitting,
    isSubmitted,
    handleClose,
    handleSubmit
  });

  useFocusAndGA({
    isOpen,
    isSubmitted,
    textareaRef,
    setGaSessionId,
    setGaClientId
  });

  useDropdownClickOutside({
    isDropdownOpen,
    modalRef,
    setIsDropdownOpen
  });

  // Keep Tab inside the dialog and hand focus back to the opener on close. Escape is
  // already handled by useKeyboardHandlers, so the trap is given no onEscape.
  useFocusTrap(modalRef, isOpen, { initialFocusRef: textareaRef });

  // The success view replaces the form (and the focused submit button with it), so move
  // focus to its heading — otherwise focus drops to <body> and nothing is announced.
  useEffect(() => {
    if (isOpen && isSubmitted) successHeadingRef.current?.focus();
  }, [isOpen, isSubmitted]);

  if (!isOpen) return null;

  const portalTarget = document.body ?? document.getElementById('root');
  if (!portalTarget) return null;

  const selectedType = feedbackTypes.find(type => type.id === feedbackType);

  return createPortal(
    // Backdrop click is a mouse convenience; keyboard users get Escape and the Close button.
    <div className="feedback-modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div
        ref={modalRef}
        className="feedback-modal"
        role="dialog"
        aria-labelledby={isSubmitted ? 'feedback-success-title' : 'feedback-title'}
        aria-modal="true"
      >
        {isSubmitted ? (
          // Success State
          <div className="feedback-success-text">
            <div className="success-icon" aria-hidden="true">
              <Check size={48} />
            </div>
            <h2 id="feedback-success-title" ref={successHeadingRef} tabIndex={-1}>
              {t('appFeedback.successTitle')}
            </h2>
            <p>{t('appFeedback.successMessage')}</p>
          </div>
        ) : (
          // Main Form
          <>
            {/* Header */}
            <div className="feedback-header">
              <h2 id="feedback-title">{t('common.sendFeedback')}</h2>
              <button
                type="button"
                className="close-button-feedback"
                onClick={handleClose}
                aria-label={t('appFeedback.closeModal')}
              >
                <X size={20} />
              </button>
            </div>

            <p className="feedback-description">
              {t('appFeedback.intro')}
            </p>

            <form onSubmit={handleSubmit} className="feedback-form">
              {/* Feedback Type Selector */}
              <div className="form-group">
                <label htmlFor="feedback-type" id="feedback-type-label">{t('appFeedback.feedbackType')}</label>
                <div className="dropdown-container">
                  <button
                    type="button"
                    id="feedback-type"
                    className="dropdown-trigger"
                    aria-labelledby="feedback-type-label feedback-type-value"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    aria-haspopup="listbox"
                    aria-expanded={isDropdownOpen}
                  >
                    <div className="dropdown-trigger-content">
                      {selectedType?.icon && <selectedType.icon size={18} className="dropdown-trigger-icon" />}
                      <span id="feedback-type-value">{selectedType?.label}</span>
                    </div>
                    <ChevronDown className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`} size={16} />
                  </button>

                  {isDropdownOpen && (
                    <div className="dropdown-menu" role="listbox" aria-labelledby="feedback-type-label">
                      {feedbackTypes.map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          className={`dropdown-item ${feedbackType === type.id ? 'selected' : ''}`}
                          onClick={() => {
                            setFeedbackType(type.id as FeedbackType);
                            setIsDropdownOpen(false);
                          }}
                          role="option"
                          aria-selected={feedbackType === type.id}
                        >
                          <div className="dropdown-item-content">
                            <type.icon size={18} className="dropdown-item-icon" />
                            <div className="dropdown-item-text">
                              <div className="item-label">{type.label}</div>
                              <div className="item-description">{type.description}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {selectedType?.helperText && (
                  <div className="helper-text">
                    {selectedType.helperText}
                  </div>
                )}
              </div>

              {/* Feedback Text */}
              <div className="form-group">
                <label htmlFor="feedback-text">{t('appFeedback.yourFeedback')}</label>
                <textarea
                  ref={textareaRef}
                  id="feedback-text"
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder={t('appFeedback.textPlaceholder')}
                  rows={6}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Metadata Section */}
              <div className="metadata-card">
                <button
                  type="button"
                  className="metadata-toggle"
                  onClick={() => setShowMetadata(!showMetadata)}
                  aria-expanded={showMetadata}
                >
                  <ChevronRight className={`toggle-arrow ${showMetadata ? 'open' : ''}`} size={16} />
                  <span>{t('appFeedback.uploadScreenshot')}</span>
                  <span className="metadata-count">
                    {t('appFeedback.itemsCount', { n: metadataItemCount() })}
                  </span>
                </button>

                {showMetadata && (
                  <MetadataContent
                    getCurrentPageName={getCurrentPageName}
                    screenshot={screenshot}
                    screenshotError={screenshotError}
                    isCapturingScreenshot={isCapturingScreenshot}
                    onFileUpload={handleFileUpload}
                    gaSessionId={gaSessionId}
                    gaClientId={gaClientId}
                    sessionId={sessionId}
                    getRolePlaySettingsMetadata={getRolePlaySettingsMetadata}
                    rolePlayData={rolePlayData}
                  />
                )}
              </div>

              {/* Actions */}
              <div className="feedback-actions">
                <button
                  type="button"
                  className="cancel-button-feedback"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="submit-button"
                  disabled={!feedbackText.trim() || isSubmitting}
                >
                  {isSubmitting && <Loader2 size={16} className="loading-spinner" />}
                  {isSubmitting ? t('appFeedback.sending') : t('common.sendFeedback')}
                </button>
              </div>

              {/* Keyboard shortcut hint */}
              {/* <div className="keyboard-hint">
                Press <kbd>Ctrl</kbd>+<kbd>Enter</kbd> to submit quickly
              </div> */}
            </form>
          </>
        )}

        {/* Toast container */}
        <div className="toast-container">
          {toasts.map(toast => (
            <ToastComponent
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </div>
      </div>
    </div>,
    portalTarget
  );
};

export default FeedbackModal;